import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';

// Map routes to sections for read permission checks
const routeToSection = {
  '/bookkeeping': 'BOOKKEEPING',
  '/sales': 'SALES',
  '/customers': 'CUSTOMER_MANAGEMENT',
  '/inventory': 'INVENTORY_MANAGEMENT',
  '/purchases': 'PURCHASES',
  '/corrections': 'CORRECTIONS',
  '/cash-flow': 'FINANCE',
  '/balance-sheet': 'FINANCE',
  '/income-statement': 'FINANCE',
  '/employees': 'EMPLOYEES',
  '/attendance': 'ATTENDANCE',
  '/company-expenses': 'COMPANY_EXPENSES',
  '/cars': 'CAR_MANAGEMENT',
  '/car-assignments': 'CAR_MANAGEMENT',
  '/master-data': 'MASTER_DATA',
  '/customer-service-order': 'CUSTOMER_SERVICE_ORDER',
  '/customer-service-complaints': 'CUSTOMER_COMPLAINTS',
  '/cso': 'CUSTOMER_SERVICE_ORDER',
  '/home': 'TODO_LIST',
  '/todos': 'TODO_LIST',
  '/tasks': 'TASK_LIST',
  '/jobs': 'JOBS',
  '/surveys': 'SURVEY',
  '/pos': 'POS'
};

const PrivateRoute = ({ children, requiredRoles = [], requireAny = false }) => {
  const { isAuthenticated, loading, userType, user } = useSelector(state => state.auth);
  const location = useLocation();

  // If authentication is still loading, show a loading spinner
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // For employees, ensure user data with roles is loaded before rendering
  // This prevents showing incorrect navigation during state rehydration
  if (userType === 'employee' && !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check read permission for current route (for employees)
  // Skip permission check for profile and login routes
  const currentPath = location.pathname;
  const isProfileRoute = currentPath === '/profile' || currentPath.startsWith('/profile');
  const isLoginRoute = currentPath === '/login' || currentPath === '/signup';
  
  if (userType === 'employee' && user && !isProfileRoute && !isLoginRoute) {
    // Helper: check if user has ANY permission for a section
    const hasAnyPermissionForSection = (sec) => {
      if (user.privileges && Array.isArray(user.privileges) && user.privileges.length > 0) {
        const privilege = user.privileges.find(p => p.section === sec);
        if (privilege) {
          return !!(
            privilege.canCreate === true || privilege.canCreate === 1 || privilege.can_create === true || privilege.can_create === 1 ||
            privilege.canRead === true || privilege.canRead === 1 || privilege.can_read === true || privilege.can_read === 1 ||
            privilege.canUpdate === true || privilege.canUpdate === 1 || privilege.can_update === true || privilege.can_update === 1 ||
            privilege.canDelete === true || privilege.canDelete === 1 || privilege.can_delete === true || privilege.can_delete === 1
          );
        }
      }
      return (user?.roles || []).includes(sec);
    };

    // Get section from current route
    let section = null;
    for (const [route, sec] of Object.entries(routeToSection)) {
      if (currentPath.startsWith(route)) {
        section = sec;
        break;
      }
    }

    // Reports: each sub-report matches REPORTS or its section
    const isReportsPath = currentPath.startsWith('/reports');
    const isSurveyPath = currentPath.startsWith('/surveys');
    let hasRoutePermission;
    if (isReportsPath) {
      if (currentPath.startsWith('/reports/inventory') || currentPath.startsWith('/reports/item-wise') || currentPath.startsWith('/reports/item-history')) {
        hasRoutePermission =
          hasAnyPermissionForSection('REPORTS') ||
          hasAnyPermissionForSection('INVENTORY_MANAGEMENT');
      } else if (currentPath.startsWith('/reports/sales')) {
        hasRoutePermission =
          hasAnyPermissionForSection('REPORTS') ||
          hasAnyPermissionForSection('SALES');
      } else if (currentPath.startsWith('/reports/customers')) {
        hasRoutePermission =
          hasAnyPermissionForSection('REPORTS') ||
          hasAnyPermissionForSection('CUSTOMER_MANAGEMENT');
      } else if (currentPath.startsWith('/reports/jobs')) {
        hasRoutePermission =
          hasAnyPermissionForSection('REPORTS') || hasAnyPermissionForSection('JOBS');
      } else if (currentPath.startsWith('/reports/cso')) {
        hasRoutePermission =
          hasAnyPermissionForSection('REPORTS') ||
          hasAnyPermissionForSection('CUSTOMER_SERVICE_ORDER');
      } else if (currentPath.startsWith('/reports/expenses')) {
        hasRoutePermission =
          hasAnyPermissionForSection('REPORTS') ||
          hasAnyPermissionForSection('COMPANY_EXPENSES');
      } else {
        hasRoutePermission =
          hasAnyPermissionForSection('REPORTS') ||
          hasAnyPermissionForSection('INVENTORY_MANAGEMENT') ||
          hasAnyPermissionForSection('CUSTOMER_MANAGEMENT') ||
          hasAnyPermissionForSection('JOBS') ||
          hasAnyPermissionForSection('CUSTOMER_SERVICE_ORDER') ||
          hasAnyPermissionForSection('COMPANY_EXPENSES') ||
          hasAnyPermissionForSection('SALES');
      }
    } else if (isSurveyPath) {
      hasRoutePermission =
        hasAnyPermissionForSection('SURVEY') ||
        hasAnyPermissionForSection('JOBS') ||
        hasAnyPermissionForSection('CUSTOMER_SERVICE_ORDER') ||
        (user?.roles || []).includes('ADMIN');
    } else if (currentPath.startsWith('/customer-service-complaints')) {
      // Dedicated section, or legacy CSO access
      hasRoutePermission =
        hasAnyPermissionForSection('CUSTOMER_COMPLAINTS') ||
        hasAnyPermissionForSection('CUSTOMER_SERVICE_ORDER');
    } else {
      hasRoutePermission = section ? hasAnyPermissionForSection(section) : false;
    }

    if (section || isReportsPath) {
      if (!hasRoutePermission) {
        // Redirect to first accessible route based on user's roles/privileges
        const userRoles = user?.roles || [];
        
        // Get accessible routes from privileges (check for ANY permission, not just read)
        const accessibleRoutes = [];
        if (user.privileges && Array.isArray(user.privileges)) {
          user.privileges.forEach(p => {
            // Check if user has ANY CRUD permission (create, read, update, or delete)
            const hasAnyPermission = !!(
              p.canCreate === true || p.canCreate === 1 || p.can_create === true || p.can_create === 1 ||
              p.canRead === true || p.canRead === 1 || p.can_read === true || p.can_read === 1 ||
              p.canUpdate === true || p.canUpdate === 1 || p.can_update === true || p.can_update === 1 ||
              p.canDelete === true || p.canDelete === 1 || p.can_delete === true || p.can_delete === 1
            );
            if (hasAnyPermission) {
              // Map section to route
              const sectionRouteMap = {
                'BOOKKEEPING': '/bookkeeping',
                'SALES': '/sales/estimate-quotation',
                'CUSTOMER_MANAGEMENT': '/customers/manage',
                'INVENTORY_MANAGEMENT': '/inventory',
                'PURCHASES': '/purchases/purchase-order',
                'FINANCE': '/cash-flow',
                'EMPLOYEES': '/employees',
                'ATTENDANCE': '/attendance',
                'COMPANY_EXPENSES': '/company-expenses',
                'CAR_MANAGEMENT': '/car-assignments',
                'MASTER_DATA': '/master-data/payment-methods',
                'CUSTOMER_SERVICE_ORDER': '/customer-service-order',
                'CUSTOMER_COMPLAINTS': '/customer-service-complaints',
                'TODO_LIST': '/home',
                'TASK_LIST': '/tasks',
                'SURVEY': '/surveys',
                'JOBS': '/jobs',
                'POS': '/pos',
                'CORRECTIONS': '/corrections/categories',
                'REPORTS': '/reports/inventory'
              };
              if (sectionRouteMap[p.section]) {
                accessibleRoutes.push(sectionRouteMap[p.section]);
              }
            }
          });
        }
        
        // Fallback to roles if no accessible routes from privileges
        const getDefaultRoute = () => {
          const route = accessibleRoutes.length > 0 ? accessibleRoutes[0] : 
            (userRoles.includes('CUSTOMER_COMPLAINTS') ? '/customer-service-complaints' :
            userRoles.includes('CUSTOMER_SERVICE_ORDER') ? '/customer-service-order' :
            userRoles.includes('TODO_LIST') ? '/home' :
            userRoles.includes('TASK_LIST') ? '/tasks' :
            userRoles.includes('BOOKKEEPING') ? '/bookkeeping' :
            userRoles.includes('SALES') ? '/sales/estimate-quotation' :
            userRoles.includes('CUSTOMER_MANAGEMENT') ? '/customers/manage' :
            userRoles.includes('INVENTORY_MANAGEMENT') ? '/inventory' :
            userRoles.includes('PURCHASES') ? '/purchases/purchase-order' :
            userRoles.includes('FINANCE') ? '/cash-flow' :
            userRoles.includes('EMPLOYEES') ? '/employees' :
            userRoles.includes('ATTENDANCE') ? '/attendance' :
            userRoles.includes('COMPANY_EXPENSES') ? '/company-expenses' :
            userRoles.includes('CAR_MANAGEMENT') ? '/car-assignments' :
            userRoles.includes('MASTER_DATA') ? '/master-data/payment-methods' :
            userRoles.includes('SURVEY') ? '/surveys' :
            userRoles.includes('JOBS') ? '/jobs' :
            userRoles.includes('POS') ? '/pos' :
            userRoles.includes('CORRECTIONS') ? '/corrections/categories' :
            userRoles.includes('REPORTS') ? '/reports/inventory' : '/profile');
          return route;
        };
        return <Navigate to={getDefaultRoute()} replace />;
      }
    }
  }

  // Check role-based access for employees (legacy support)
  if (userType === 'employee' && requiredRoles.length > 0) {
    const userRoles = user?.roles || [];
    
    // Check if user has required roles or privileges
    let hasAccess = false;
    
      // First check roles (legacy support)
      const hasRequiredRoles = requireAny
        ? requiredRoles.some(role => userRoles.includes(role)) // Any of the roles
        : requiredRoles.every(role => userRoles.includes(role)); // All of the roles
      
      // Then check privileges (new system) - check for ANY permission, not just read
      if (!hasRequiredRoles && user?.privileges && Array.isArray(user.privileges)) {
        // Map requiredRoles to sections for privilege checking
        const roleToSectionMap = {
          'ADMIN': null, // ADMIN doesn't map to a section, it's a special role
          'CAR_MANAGEMENT': 'CAR_MANAGEMENT',
          'COMPANY_EXPENSES': 'COMPANY_EXPENSES',
          'CUSTOMER_SERVICE_ORDER': 'CUSTOMER_SERVICE_ORDER',
          'CUSTOMER_COMPLAINTS': 'CUSTOMER_COMPLAINTS',
          'BOOKKEEPING': 'BOOKKEEPING',
          'SALES': 'SALES',
          'CUSTOMER_MANAGEMENT': 'CUSTOMER_MANAGEMENT',
          'INVENTORY_MANAGEMENT': 'INVENTORY_MANAGEMENT',
          'PURCHASES': 'PURCHASES',
          'FINANCE': 'FINANCE',
          'EMPLOYEES': 'EMPLOYEES',
          'ATTENDANCE': 'ATTENDANCE',
          'MASTER_DATA': 'MASTER_DATA',
          'TODO_LIST': 'TODO_LIST',
          'TASK_LIST': 'TASK_LIST',
          'SURVEY': 'SURVEY',
          'JOBS': 'JOBS',
          'POS': 'POS',
          'REPORTS': 'REPORTS',
          'CORRECTIONS': 'CORRECTIONS'
        };
        
        if (requireAny) {
          // Check if user has ANY permission (create, read, update, or delete) for any of the required roles' sections
          hasAccess = requiredRoles.some(role => {
            const section = roleToSectionMap[role];
            if (!section) return false; // Skip ADMIN and unmapped roles
            const privilege = user.privileges.find(p => p.section === section);
            if (!privilege) return false;
            // Check if user has ANY CRUD permission
            return !!(
              privilege.canCreate === true || privilege.canCreate === 1 || privilege.can_create === true || privilege.can_create === 1 ||
              privilege.canRead === true || privilege.canRead === 1 || privilege.can_read === true || privilege.can_read === 1 ||
              privilege.canUpdate === true || privilege.canUpdate === 1 || privilege.can_update === true || privilege.can_update === 1 ||
              privilege.canDelete === true || privilege.canDelete === 1 || privilege.can_delete === true || privilege.can_delete === 1
            );
          });
        } else {
          // Check if user has ANY permission for all required roles' sections
          hasAccess = requiredRoles.every(role => {
            const section = roleToSectionMap[role];
            if (!section) return false; // Skip ADMIN and unmapped roles
            const privilege = user.privileges.find(p => p.section === section);
            if (!privilege) return false;
            // Check if user has ANY CRUD permission
            return !!(
              privilege.canCreate === true || privilege.canCreate === 1 || privilege.can_create === true || privilege.can_create === 1 ||
              privilege.canRead === true || privilege.canRead === 1 || privilege.can_read === true || privilege.can_read === 1 ||
              privilege.canUpdate === true || privilege.canUpdate === 1 || privilege.can_update === true || privilege.can_update === 1 ||
              privilege.canDelete === true || privilege.canDelete === 1 || privilege.can_delete === true || privilege.can_delete === 1
            );
          });
        }
      } else {
        hasAccess = hasRequiredRoles;
      }

    if (!hasAccess) {
      // Find the first accessible route based on user's roles/privileges
      // First, try to get accessible routes from privileges (check for ANY permission)
      const accessibleRoutes = [];
      if (user?.privileges && Array.isArray(user.privileges)) {
        user.privileges.forEach(p => {
          // Check if user has ANY CRUD permission (create, read, update, or delete)
          const hasAnyPermission = !!(
            p.canCreate === true || p.canCreate === 1 || p.can_create === true || p.can_create === 1 ||
            p.canRead === true || p.canRead === 1 || p.can_read === true || p.can_read === 1 ||
            p.canUpdate === true || p.canUpdate === 1 || p.can_update === true || p.can_update === 1 ||
            p.canDelete === true || p.canDelete === 1 || p.can_delete === true || p.can_delete === 1
          );
          if (hasAnyPermission) {
            const sectionRouteMap = {
              'BOOKKEEPING': '/bookkeeping',
              'SALES': '/sales/estimate-quotation',
              'CUSTOMER_MANAGEMENT': '/customers/manage',
              'INVENTORY_MANAGEMENT': '/inventory',
              'PURCHASES': '/purchases/purchase-order',
              'FINANCE': '/cash-flow',
              'EMPLOYEES': '/employees',
              'ATTENDANCE': '/attendance',
              'COMPANY_EXPENSES': '/company-expenses',
              'CAR_MANAGEMENT': '/car-assignments',
              'MASTER_DATA': '/master-data/payment-methods',
              'CUSTOMER_SERVICE_ORDER': '/customer-service-order',
              'CUSTOMER_COMPLAINTS': '/customer-service-complaints',
              'TODO_LIST': '/home',
              'TASK_LIST': '/tasks',
              'SURVEY': '/surveys',
              'JOBS': '/jobs',
              'POS': '/pos',
              'CORRECTIONS': '/corrections/categories',
              'REPORTS': '/reports/inventory'
            };
            if (sectionRouteMap[p.section]) {
              accessibleRoutes.push(sectionRouteMap[p.section]);
            }
          }
        });
      }
      
      const getDefaultRoute = () => {
        const route = accessibleRoutes.length > 0 ? accessibleRoutes[0] : 
          (userRoles.includes('CUSTOMER_COMPLAINTS') ? '/customer-service-complaints' :
          userRoles.includes('CUSTOMER_SERVICE_ORDER') ? '/customer-service-order' :
          userRoles.includes('TODO_LIST') ? '/home' :
          userRoles.includes('TASK_LIST') ? '/tasks' :
          userRoles.includes('BOOKKEEPING') ? '/bookkeeping' :
          userRoles.includes('SALES') ? '/sales/estimate-quotation' :
          userRoles.includes('CUSTOMER_MANAGEMENT') ? '/customers/manage' :
          userRoles.includes('INVENTORY_MANAGEMENT') ? '/inventory' :
          userRoles.includes('PURCHASES') ? '/purchases/purchase-order' :
          userRoles.includes('FINANCE') ? '/cash-flow' :
          userRoles.includes('EMPLOYEES') ? '/employees' :
          userRoles.includes('ATTENDANCE') ? '/attendance' :
          userRoles.includes('COMPANY_EXPENSES') ? '/company-expenses' :
          userRoles.includes('CAR_MANAGEMENT') ? '/car-assignments' :
          userRoles.includes('MASTER_DATA') ? '/master-data/payment-methods' :
          userRoles.includes('SURVEY') ? '/surveys' :
          userRoles.includes('JOBS') ? '/jobs' :
          userRoles.includes('POS') ? '/pos' :
          userRoles.includes('CORRECTIONS') ? '/corrections/categories' :
          userRoles.includes('REPORTS') ? '/reports/inventory' : '/profile');
        return route;
      };

      return <Navigate to={getDefaultRoute()} replace />;
    }
  }

  // If authenticated and has access, render the protected component
  return children;
};

export default PrivateRoute; 